// src/features/calendar/time.ts
// Pure date/time and calendar-domain helpers — no React imports.

import type { Entity } from '@core/types/entity';
import type { Actor } from '@core/types/actor';
import type {
  CalendarEvent,
  RecurrenceRule,
  RecurrenceWeekday,
  WorkingPeriod,
} from '@core/types/calendar';

// ---------------------------------------------------------------------------
// Entity helpers
// ---------------------------------------------------------------------------

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

/** Build an Actor reference for a profile id. */
export function makeActor(profileId: string, displayName: string, scope: ScopeIds): Actor {
  return {
    entity: makeEntity(profileId, 'profile', scope),
    display_name: displayName,
    avatar: '',
  };
}

// ---------------------------------------------------------------------------
// Date math
// ---------------------------------------------------------------------------

export const DAY_MS = 24 * 60 * 60 * 1000;

export function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

export function endOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(23, 59, 59, 999);
  return r;
}

/** Monday-based start of week. */
export function startOfWeek(d: Date): Date {
  const r = startOfDay(d);
  const dow = (r.getDay() + 6) % 7; // Mon=0 … Sun=6
  r.setDate(r.getDate() - dow);
  return r;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export type CalendarViewMode = 'day' | 'week' | 'month' | 'agenda';

/** Visible [start, end) range for a view anchored at `anchor`. */
export function viewRange(view: CalendarViewMode, anchor: Date): { start: Date; end: Date } {
  switch (view) {
    case 'day':
      return { start: startOfDay(anchor), end: addDays(startOfDay(anchor), 1) };
    case 'week':
      return { start: startOfWeek(anchor), end: addDays(startOfWeek(anchor), 7) };
    case 'agenda':
      return { start: startOfDay(anchor), end: addDays(startOfDay(anchor), 30) };
    case 'month':
    default: {
      const first = startOfMonth(anchor);
      const gridStart = startOfWeek(first);
      return { start: gridStart, end: addDays(gridStart, 42) };
    }
  }
}

export function shiftAnchor(view: CalendarViewMode, anchor: Date, dir: 1 | -1): Date {
  switch (view) {
    case 'day':
      return addDays(anchor, dir);
    case 'week':
      return addDays(anchor, 7 * dir);
    case 'agenda':
      return addDays(anchor, 30 * dir);
    case 'month':
    default:
      return addMonths(anchor, dir);
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function fmtTime(iso: string | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function fmtDate(iso: string | Date | undefined, locale: string): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateTime(iso: string | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function fmtMonthTitle(anchor: Date, locale: string): string {
  return anchor.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
}

/** `<input type="date">` value for a Date. */
export function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `<input type="time">` value for a Date. */
export function toTimeInputValue(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Combine date input + time input values into an ISO timestamp. */
export function dateTimeToIso(dateValue: string, timeValue: string): string {
  const [y, m, d] = dateValue.split('-').map(Number);
  const [hh, mm] = (timeValue || '00:00').split(':').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0).toISOString();
}

// ---------------------------------------------------------------------------
// Durations. The generated TS contract says proto-JSON string ("3600s"), but
// the Go DTOs behind the BFF marshal time.Duration as int64 NANOSECONDS — a
// string on the wire is a 400, and reads come back numeric. Until the
// generator unifies the contract, speak nanoseconds and accept both shapes.
// ---------------------------------------------------------------------------

const NANOS_PER_MINUTE = 60_000_000_000;

export function durationToMinutes(dur: string | number | undefined): number | '' {
  if (dur === undefined || dur === null || dur === '') return '';
  if (typeof dur === 'number') return Math.round(dur / NANOS_PER_MINUTE);
  const secs = parseInt(dur, 10);
  if (Number.isNaN(secs)) return '';
  return Math.round(secs / 60);
}

export function minutesToDuration(minutes: number | ''): string | undefined {
  if (minutes === '' || Number.isNaN(minutes)) return undefined;
  return Math.round(minutes * NANOS_PER_MINUTE) as unknown as string;
}

// ---------------------------------------------------------------------------
// Weekdays
// ---------------------------------------------------------------------------

/** Monday-first proto weekday order used across editors. */
export const WEEKDAYS: RecurrenceWeekday[] = [
  'RECURRENCE_WEEKDAY_MONDAY',
  'RECURRENCE_WEEKDAY_TUESDAY',
  'RECURRENCE_WEEKDAY_WEDNESDAY',
  'RECURRENCE_WEEKDAY_THURSDAY',
  'RECURRENCE_WEEKDAY_FRIDAY',
  'RECURRENCE_WEEKDAY_SATURDAY',
  'RECURRENCE_WEEKDAY_SUNDAY',
];

/** i18n suffix for each weekday, e.g. calendar.weekday.mon */
export const WEEKDAY_KEYS: Record<RecurrenceWeekday, string> = {
  RECURRENCE_WEEKDAY_UNSPECIFIED: 'mon',
  RECURRENCE_WEEKDAY_MONDAY: 'mon',
  RECURRENCE_WEEKDAY_TUESDAY: 'tue',
  RECURRENCE_WEEKDAY_WEDNESDAY: 'wed',
  RECURRENCE_WEEKDAY_THURSDAY: 'thu',
  RECURRENCE_WEEKDAY_FRIDAY: 'fri',
  RECURRENCE_WEEKDAY_SATURDAY: 'sat',
  RECURRENCE_WEEKDAY_SUNDAY: 'sun',
};

// ---------------------------------------------------------------------------
// Events on the grid
// ---------------------------------------------------------------------------

export interface EventsByDay {
  [dayKey: string]: CalendarEvent[];
}

export function dayKey(d: Date): string {
  return toDateInputValue(d);
}

/**
 * Group events into per-day buckets across [start, end).
 * Multi-day events appear in each day they touch.
 */
export function groupEventsByDay(events: CalendarEvent[], start: Date, end: Date): EventsByDay {
  const buckets: EventsByDay = {};
  for (const ev of events) {
    if (!ev.start_time) continue;
    const evStart = new Date(ev.start_time);
    const evEnd = ev.end_time ? new Date(ev.end_time) : evStart;
    let cursor = startOfDay(evStart < start ? start : evStart);
    const last = evEnd > end ? end : evEnd;
    while (cursor < last || isSameDay(cursor, evStart)) {
      if (cursor >= start && cursor < end) {
        const key = dayKey(cursor);
        (buckets[key] ??= []).push(ev);
      }
      cursor = addDays(cursor, 1);
      if (cursor > end) break;
    }
  }
  for (const key of Object.keys(buckets)) {
    buckets[key].sort((a, b) => {
      if (a.is_all_day !== b.is_all_day) return a.is_all_day ? -1 : 1;
      return (a.start_time ?? '').localeCompare(b.start_time ?? '');
    });
  }
  return buckets;
}

/** Vertical placement of a timed event inside a day column (minutes from midnight). */
export function eventVerticalBounds(ev: CalendarEvent, day: Date): { top: number; height: number } {
  const dayStart = startOfDay(day).getTime();
  const s = ev.start_time ? new Date(ev.start_time).getTime() : dayStart;
  const e = ev.end_time ? new Date(ev.end_time).getTime() : s + 30 * 60000;
  const top = Math.max(0, (s - dayStart) / 60000);
  const bottom = Math.min(24 * 60, (e - dayStart) / 60000);
  return { top, height: Math.max(20, bottom - top) };
}

// ---------------------------------------------------------------------------
// Recurrence
// ---------------------------------------------------------------------------

export function isRecurring(ev: CalendarEvent | null | undefined): boolean {
  return !!ev && (!!ev.recurrence_rule || ev.is_recurring_instance || !!ev.recurring_event_id);
}

/** Short human summary parameters for a recurrence rule; rendering is done with i18n. */
export function recurrenceFrequencyKey(rule: RecurrenceRule | undefined): string | null {
  switch (rule?.frequency) {
    case 'RECURRENCE_FREQUENCY_DAILY': return 'daily';
    case 'RECURRENCE_FREQUENCY_WEEKLY': return 'weekly';
    case 'RECURRENCE_FREQUENCY_MONTHLY': return 'monthly';
    case 'RECURRENCE_FREQUENCY_YEARLY': return 'yearly';
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Working periods
// ---------------------------------------------------------------------------

export interface DayPeriods {
  day: RecurrenceWeekday;
  ranges: { start: string; end: string }[];
}

/** Expand a flat WorkingPeriod list into a Monday-first 7-day structure. */
export function periodsToWeek(periods: WorkingPeriod[]): DayPeriods[] {
  return WEEKDAYS.map((day) => ({
    day,
    ranges: periods
      .filter((p) => p.day_of_week === day)
      .map((p) => ({ start: p.start_time, end: p.end_time })),
  }));
}

export function weekToPeriods(week: DayPeriods[]): WorkingPeriod[] {
  const out: WorkingPeriod[] = [];
  for (const d of week) {
    for (const r of d.ranges) {
      if (r.start && r.end) out.push({ day_of_week: d.day, start_time: r.start, end_time: r.end });
    }
  }
  return out;
}

export const DEFAULT_WORK_RANGE = { start: '09:00', end: '18:00' };

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** The shape of the public booking URL for a slug. */
export function publicBookingUrl(slug: string): string {
  return `${window.location.origin}/book/${slug}`;
}

export function eventColor(ev: CalendarEvent, calendarColors: Map<string, string>): string | undefined {
  return ev.color || (ev.calendar?.id ? calendarColors.get(ev.calendar.id) : undefined);
}

/** Extract an error message suitable for display. */
export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}
